import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { GIVEAWAY_CAMPAIGN_HARD_OFF, GIVEAWAY_EVENT_SLUG } from "../lib/giveawayCampaign";
import { Check, Instagram, PartyPopper } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const INSTAGRAM_HANDLE = "@z_cars_rentals";
const HASHTAGS = "#ZCars #ZCarsGiveaway #Contest";

function FieldError({ message }) {
  if (!message) return null;
  return (
    <p className="text-sm mt-1.5" style={{ color: "var(--zc-danger)" }} role="alert">
      {message}
    </p>
  );
}

export default function Giveaway() {
  const [step, setStep] = useState("form");
  const [entryId, setEntryId] = useState("");
  const [ticketSerial, setTicketSerial] = useState("");
  const [purchaserName, setPurchaserName] = useState("");
  const [fullName, setFullName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [campaignEnabled, setCampaignEnabled] = useState(null);

  const [suggestions, setSuggestions] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimer = useRef(null);
  const purchaserWrapRef = useRef(null);

  const eventPath = `${API}/giveaway/${GIVEAWAY_EVENT_SLUG}`;

  useEffect(() => {
    if (GIVEAWAY_CAMPAIGN_HARD_OFF) {
      setCampaignEnabled(false);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${eventPath}/info`);
        if (alive) setCampaignEnabled(r.data?.enabled !== false);
      } catch {
        if (alive) setCampaignEnabled(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [eventPath]);

  const fetchSuggestions = useCallback(
    (q) => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(async () => {
        const query = (q || "").trim();
        if (query.length < 2) {
          setSuggestions([]);
          setSearchLoading(false);
          return;
        }
        setSearchLoading(true);
        try {
          const r = await axios.get(`${eventPath}/purchaser-search`, { params: { q: query } });
          setSuggestions(r.data?.matches || []);
        } catch {
          setSuggestions([]);
        } finally {
          setSearchLoading(false);
        }
      }, 280);
    },
    [eventPath],
  );

  useEffect(() => {
    const onDocClick = (e) => {
      if (purchaserWrapRef.current && !purchaserWrapRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const onPurchaserChange = (value) => {
    setPurchaserName(value);
    setSearchOpen(true);
    fetchSuggestions(value);
  };

  const pickPurchaser = (name) => {
    setPurchaserName(name);
    setSearchOpen(false);
    setSuggestions([]);
  };

  const validate = () => {
    const err = {};
    if (!ticketSerial.trim()) err.ticket_serial = "Enter your ticket serial number.";
    if (!purchaserName.trim() || purchaserName.trim().length < 2) {
      err.purchaser_name = "Who purchased this ticket?";
    }
    if (!fullName.trim() || fullName.trim().length < 2) err.full_name = "Full name is required.";
    if (!mobile.trim() || mobile.trim().length < 7) err.mobile = "Enter a valid mobile number.";
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      err.email = "Enter a valid email.";
    }
    setFieldErrors(err);
    return Object.keys(err).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${eventPath}/enter`, {
        ticket_serial: ticketSerial.trim(),
        purchaser_name: purchaserName.trim(),
        full_name: fullName.trim(),
        mobile: mobile.trim(),
        email: email.trim(),
      });
      setEntryId(r.data?.entry_id || "");
      setStep("instructions");
      if (r.data?.deduplicated) {
        toast.message("This ticket was already enrolled — here are your next steps.");
      } else {
        const left = r.data?.tickets_remaining_for_purchaser;
        const extra =
          typeof left === "number"
            ? left > 0
              ? ` ${left} ticket(s) still available for this purchaser.`
              : " All tickets for this purchaser are now enrolled."
            : "";
        toast.success(`You're enrolled! Complete the social steps below.${extra}`);
      }
    } catch (ex) {
      const detail = ex.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Could not submit entry. Try again.";
      toast.error(msg);
      if (msg.toLowerCase().includes("ticket") || msg.toLowerCase().includes("serial")) {
        setFieldErrors((prev) => ({ ...prev, ticket_serial: msg }));
      } else if (msg.toLowerCase().includes("name")) {
        setFieldErrors((prev) => ({ ...prev, purchaser_name: msg }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (campaignEnabled === null) {
    return (
      <div className="max-w-[640px] mx-auto px-4 sm:px-6 lg:px-10 py-12 text-[var(--zc-text-2)]" data-testid="giveaway-loading">
        Loading…
      </div>
    );
  }

  if (!campaignEnabled) {
    return (
      <div className="max-w-[640px] mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="giveaway-disabled">
        <div className="eyebrow mb-2">Apna Caledon · Z-Cars</div>
        <h1 className="zc-headline text-4xl md:text-5xl">Giveaway paused</h1>
        <p className="text-[var(--zc-text-2)] mt-4">
          This campaign is not open for entries right now. Check back during the event or visit the Z-Cars booth.
        </p>
        <Link to="/" className="zc-btn-primary inline-flex h-11 px-6 text-sm mt-8">
          Back to home
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-[640px] mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="giveaway-page">
      <div className="eyebrow mb-2">Apna Caledon · Z-Cars</div>
      <h1 className="zc-headline text-4xl md:text-5xl flex items-center gap-2 flex-wrap">
        <PartyPopper size={32} className="text-[var(--zc-accent)]" aria-hidden />
        WIN A 65&quot; TV
      </h1>

      {step === "form" && (
        <form className="mt-10 space-y-6" onSubmit={submit} noValidate data-testid="giveaway-form">
          <div>
            <label htmlFor="giveaway-ticket-serial" className="block text-sm font-medium text-[var(--zc-text-2)] mb-1.5">
              🎟 Ticket Serial Number
            </label>
            <input
              id="giveaway-ticket-serial"
              className="zc-input w-full"
              inputMode="numeric"
              autoComplete="off"
              placeholder="327"
              value={ticketSerial}
              onChange={(ev) => setTicketSerial(ev.target.value)}
              data-testid="giveaway-ticket-serial"
            />
            <p className="text-xs text-[var(--zc-text-3)] mt-1.5">
              From your physical ticket — we do not match this number to the buyer name.
            </p>
            <FieldError message={fieldErrors.ticket_serial} />
          </div>

          <div ref={purchaserWrapRef} className="relative">
            <label htmlFor="giveaway-purchaser" className="block text-sm font-medium text-[var(--zc-text-2)] mb-1.5">
              Who purchased this ticket?
            </label>
            <input
              id="giveaway-purchaser"
              className="zc-input w-full"
              autoComplete="off"
              placeholder="Start typing name..."
              value={purchaserName}
              onChange={(ev) => onPurchaserChange(ev.target.value)}
              onFocus={() => setSearchOpen(true)}
              data-testid="giveaway-purchaser"
            />
            {searchOpen && (suggestions.length > 0 || searchLoading) && (
              <ul
                className="absolute z-20 left-0 right-0 mt-1 zc-card py-1 shadow-lg max-h-48 overflow-y-auto"
                role="listbox"
                data-testid="giveaway-purchaser-suggestions"
              >
                {searchLoading && (
                  <li className="px-4 py-2 text-sm text-[var(--zc-text-3)]">Searching…</li>
                )}
                {!searchLoading &&
                  suggestions.map((row) => (
                    <li key={row.purchaser_name}>
                      <button
                        type="button"
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-[var(--zc-bg-3)] flex items-center justify-between gap-2"
                        onClick={() => pickPurchaser(row.purchaser_name)}
                      >
                        <span>{row.purchaser_name}</span>
                        <span className="text-xs text-[var(--zc-text-3)] shrink-0">
                          {row.tickets_remaining} left
                        </span>
                      </button>
                    </li>
                  ))}
              </ul>
            )}
            {purchaserName.trim().length >= 2 &&
              suggestions.some(
                (n) => n.purchaser_name?.toLowerCase() === purchaserName.trim().toLowerCase(),
              ) && (
                <p className="text-sm mt-1.5 flex items-center gap-1 text-[var(--zc-success)]">
                  <Check size={14} /> {purchaserName.trim()} matched (
                  {suggestions.find(
                    (n) => n.purchaser_name?.toLowerCase() === purchaserName.trim().toLowerCase(),
                  )?.tickets_remaining ?? "—"}{" "}
                  ticket(s) left)
                </p>
              )}
            <FieldError message={fieldErrors.purchaser_name} />
          </div>

          <div className="pt-2">
            <div className="eyebrow mb-4">Your details</div>
            <div className="space-y-4">
              <div>
                <label htmlFor="giveaway-full-name" className="block text-sm font-medium text-[var(--zc-text-2)] mb-1.5">
                  Full Name
                </label>
                <input
                  id="giveaway-full-name"
                  className="zc-input w-full"
                  autoComplete="name"
                  value={fullName}
                  onChange={(ev) => setFullName(ev.target.value)}
                  data-testid="giveaway-full-name"
                />
                <FieldError message={fieldErrors.full_name} />
              </div>
              <div>
                <label htmlFor="giveaway-mobile" className="block text-sm font-medium text-[var(--zc-text-2)] mb-1.5">
                  Mobile
                </label>
                <input
                  id="giveaway-mobile"
                  className="zc-input w-full"
                  type="tel"
                  autoComplete="tel"
                  value={mobile}
                  onChange={(ev) => setMobile(ev.target.value)}
                  data-testid="giveaway-mobile"
                />
                <FieldError message={fieldErrors.mobile} />
              </div>
              <div>
                <label htmlFor="giveaway-email" className="block text-sm font-medium text-[var(--zc-text-2)] mb-1.5">
                  Email
                </label>
                <input
                  id="giveaway-email"
                  className="zc-input w-full"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  data-testid="giveaway-email"
                />
                <FieldError message={fieldErrors.email} />
              </div>
            </div>
          </div>

          <button
            type="submit"
            className="zc-btn-primary w-full h-12 text-sm mt-4"
            disabled={submitting}
            data-testid="giveaway-continue"
          >
            {submitting ? "Submitting…" : "CONTINUE →"}
          </button>
        </form>
      )}

      {step === "instructions" && (
        <div className="mt-10 space-y-6" data-testid="giveaway-instructions">
          <div className="zc-card p-6" style={{ background: "var(--zc-gradient-soft)" }}>
            <p className="text-[var(--zc-text)] font-semibold">Almost done — one last step to qualify!</p>
            {entryId && (
              <p className="text-sm text-[var(--zc-text-2)] mt-2">
                Enrollment ref: <span className="font-mono">{entryId}</span>
              </p>
            )}
          </div>

          <ol className="space-y-4 text-[var(--zc-text)] list-decimal list-inside marker:font-semibold">
            <li className="leading-relaxed">
              Follow{" "}
              <a
                href="https://www.instagram.com/z_cars_rentals/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold underline decoration-[var(--zc-accent)]"
              >
                {INSTAGRAM_HANDLE}
              </a>{" "}
              on Instagram and like the{" "}
              <a
                href="https://www.facebook.com/zcarsrentals"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold underline decoration-[var(--zc-secondary)]"
              >
                Z-Cars Facebook page
              </a>
              .
            </li>
            <li className="leading-relaxed">
              Post a <strong>public</strong> selfie at the Z-Cars banner on Instagram, tagging{" "}
              <span className="inline-flex items-center gap-1 font-semibold">
                <Instagram size={14} aria-hidden />
                {INSTAGRAM_HANDLE}
              </span>{" "}
              with {HASHTAGS}.
            </li>
          </ol>

          <div
            className="zc-card p-6 border-2"
            style={{ borderColor: "var(--zc-accent)" }}
            data-testid="giveaway-booth-staff"
          >
            <p className="eyebrow text-[var(--zc-accent)] mb-2">At the event</p>
            <p className="text-lg font-semibold text-[var(--zc-text)]">
              Show your Instagram post to booth staff to confirm your entry for the 65&quot; TV draw.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
